import React, { FC, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { Button } from "../../../components/Button/Button";
import { InputPanel } from "../../../components/Brief/InputPanel";
import { BrandProject } from "./constants";
import { cancelBlockedReason, cancelProject, projectStatusErrorMessage } from "./projectStatusActions";

interface Props {
    project: BrandProject;
    onClose: () => void;
    onComplete: () => void;
}

/**
 * Cancel a project from ANY status — the internal team's standing ability,
 * previously only reachable by picking Cancelled in the edit modal's status
 * dropdown. A reason is required: it is shown on the project, goes to the
 * brand in the cancellation notification, and the API rejects a cancel
 * without one.
 *
 * The blocked case is shown rather than hidden: callers already gate the menu
 * item on canCancelProject, so landing here with a reason means the project's
 * selection was committed while the dialog sat open. Saying so beats a 423.
 */
export const CancelProjectDialog: FC<Props> = ({ project, onClose, onComplete }) => {
    const [submitting, setSubmitting] = useState(false);
    const blockedReason = cancelBlockedReason(project);

    const handleCancelProject = async (reason: string) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await cancelProject(project, reason);
            toast.success("Project cancelled.");
            // onComplete unmounts this dialog, so nothing may touch state after it.
            onComplete();
            return;
        } catch (err) {
            toast.error(projectStatusErrorMessage(err));
        }
        // Reached only on failure: re-enable the button so the user can retry.
        setSubmitting(false);
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h5" className="!text-[20px]">
                    Cancel this project?
                </Typography>
            </DialogTitle>
            <Formik initialValues={{ reason: "" }} onSubmit={(values) => handleCancelProject(values.reason)}>
                {({ values }) => (
                    <Form>
                        <DialogContent className="border-y border-border-token">
                            <Box className="flex flex-col gap-3 py-4">
                                {blockedReason ? (
                                    <Typography className="!text-text-2 !font-circular !text-[16px] !leading-[24px]">
                                        {blockedReason}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography className="!text-text-2 !font-circular !text-[16px] !leading-[24px]">
                                            <b className="text-text-1">{project.title}</b> will move from{" "}
                                            {project.status} to Cancelled and the brand will be notified. Any
                                            work in progress — brief, expert casting, scheduling — stops here.
                                        </Typography>
                                        <Typography className="!text-text-2 !font-circular !text-[14px]">
                                            Only this project is cancelled; other projects on the same order and
                                            selection are untouched. You can restore it later from the project
                                            menu: it goes back to {project.status} if it is alone on its
                                            selection, or rejoins the other projects on its selection where
                                            they are.
                                        </Typography>
                                        <InputPanel
                                            name="reason"
                                            rows={3}
                                            body="Why is this project being cancelled? The brand will see this."
                                            placeholder="Reason for cancelling"
                                        />
                                    </>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions className="!px-6 !py-4">
                            <Box className="flex items-center gap-6">
                                <Button
                                    variant="default"
                                    disabled={submitting}
                                    onClick={onClose}
                                    className="!bg-surface-2 hover:!bg-track !text-text-1 h-[40px] !py-0 !px-4 !min-w-fit"
                                >
                                    {blockedReason ? "Close" : "Keep project"}
                                </Button>
                                {!blockedReason && (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={submitting || !values.reason.trim()}
                                        className="h-[40px] flex items-center !py-0 !px-4 !min-w-fit !bg-[#EB3E33] hover:!bg-[#d82d21]"
                                    >
                                        {submitting ? "Cancelling..." : "Cancel project"}
                                    </Button>
                                )}
                            </Box>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};
